import {RecurrenceBadge} from "../tasks/components/task-recurrence-badge";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {Message} from "primereact/message";
import {useTask} from "../../models/task/use-task";
import {mergeWorkspaceTasks, upsertWorkspaceTask, workspaceGraphSelectors} from "../../system/workspace-graph-slice";
import {filterMyTasksScope} from "../../models/task/my-tasks-scope";
import {TaskDateTime} from "../tasks/components/task-time-range";
import {MobileEmpty, MobileError, MobileLoading, MobilePageHeader, PriorityTag, ProjectIndicator} from "./mobile-common";
import {createMobileTaskFilters, entityKey, filterMobileTasks, getPriority, sortTasks} from "./mobile-utils";
import TaskFocusControls from "../../system/focus/task-focus-controls";
import {RichTextView, richTextToPlain} from "../../system/components/rich-text";

export default function MobileTasks({detailId, navigate, translate, readOnly}) {
    const dispatch = useDispatch();
    const taskApi = useTask();
    const workspaceTasks = useSelector(workspaceGraphSelectors.tasks);
    const workspaceMembership = useSelector((state) => state.app.workspaceMembershipData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState(() => createMobileTaskFilters());
    const [updatingTaskId, setUpdatingTaskId] = useState(null);
    const [mutationError, setMutationError] = useState(false);
    const workspaceId = workspaceMembership?.workspace?.id;

    const refresh = useCallback(async () => {
        if (!workspaceId) return;
        setLoading(true);
        setError(null);
        try {
            const result = await taskApi.getTasks(null, workspaceId, {scope: "my-tasks"});
            dispatch(mergeWorkspaceTasks(result || []));
        } catch (requestError) {
            setError(requestError);
        } finally {
            setLoading(false);
        }
        // API hook methods intentionally have unstable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, workspaceId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const tasks = useMemo(() => filterMyTasksScope(
        workspaceTasks,
        workspaceMembership?.workspace,
        workspaceMembership,
    ), [workspaceMembership, workspaceTasks]);

    if (detailId) {
        return <MobileTaskDetail detailId={detailId} tasks={tasks} refresh={refresh} navigate={navigate} translate={translate} readOnly={readOnly}/>;
    }

    const filtered = sortTasks(filterMobileTasks(tasks, filters));
    const toggleDone = async (task) => {
        if (readOnly || updatingTaskId) return;
        const taskId = entityKey(task);
        const nextStatus = task.taskStatus === "done" ? "todo" : "done";
        setUpdatingTaskId(taskId);
        setMutationError(false);
        try {
            const updated = await taskApi.updateTask(taskId, {
                taskStatus: nextStatus,
                doneAt: nextStatus === "done" ? Date.now() : null,
            });
            if (updated) dispatch(upsertWorkspaceTask(updated));
            else setMutationError(true);
        } finally {
            setUpdatingTaskId(null);
        }
    };

    return (
        <div className="flex flex-column gap-3">
            <MobilePageHeader title={translate("mobile.tasks.title")} description={translate("mobile.tasks.description")}/>
            <MobileTaskFilter filters={filters} onChange={setFilters} translate={translate}/>
            {mutationError && <Message severity="error" className="text-xs" text={translate("mobile.common.mutation-error")}/>} 
            {error ? <MobileError message={translate("mobile.common.load-error")} retryLabel={translate("mobile.common.retry")} onRetry={refresh}/>
                : loading && !tasks.length ? <MobileLoading/>
                    : filtered.length ? <div className="flex flex-column gap-2">{filtered.map((task) => (
                        <TaskCard key={entityKey(task)} task={task} loading={String(updatingTaskId) === String(entityKey(task))} navigate={navigate} onToggleDone={toggleDone} readOnly={readOnly} translate={translate}/>
                    ))}</div>
                        : <MobileEmpty icon="pi pi-check-circle" title={translate(filters.search.trim() ? "mobile.tasks.search-empty" : tasks.length ? "mobile.tasks.filter-empty" : "mobile.tasks.empty")} description={translate(filters.search.trim() ? "mobile.tasks.search-empty-description" : "mobile.tasks.empty-description")}/>} 
        </div>
    );
}

export function MobileTaskFilter({filters, onChange, translate}) {
    const scopeOptions = ["today", "next", "done"].map((value) => ({value, label: translate(`mobile.tasks.segment.${value}`)}));
    const secondaryOptions = ["all", "high-priority", "overdue", "waiting"].map((value) => ({value, label: translate(value === "all" ? "mobile.tasks.segment.all" : `mobile.tasks.filter.${value}`), disabled: ["high-priority", "waiting"].includes(value)}));
    return (
        <section className="flex flex-column gap-2" aria-label={translate("mobile.filters.title")}>
            <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-search"/>
                <InputText className="w-full h-2rem text-xs text-right surface-card border-1 border-200 mobile-brand-radius shadow-none" value={filters.search} placeholder={translate("mobile.tasks.search-placeholder")} onChange={(event) => onChange((current) => ({...current, search: event.target.value}))}/>
            </IconField>
            <div className="w-full flex align-items-center gap-1 surface-100 mobile-brand-radius p-1" role="group">
                {scopeOptions.map((option) => {
                    const active = filters.scope === option.value;
                    return <button key={option.value} type="button" aria-pressed={active} className={`flex-1 min-w-0 h-2rem flex align-items-center justify-content-center border-none mobile-brand-radius text-xs cursor-pointer ${active ? "surface-card text-900 mobile-brand-shadow font-bold" : "surface-100 text-500"}`} onClick={() => onChange((current) => ({...current, scope: option.value}))}>{option.label}</button>;
                })}
            </div>
            <div className="w-full flex align-items-center gap-2 overflow-x-auto" role="group">
                {secondaryOptions.map((option) => {
                    const active = filters.secondary === option.value;
                    return <button key={option.value} type="button" disabled={option.disabled} aria-pressed={active} className={`flex-1 h-2rem flex align-items-center justify-content-center px-3 border-none mobile-brand-radius text-2xs white-space-nowrap ${option.disabled ? "cursor-not-allowed" : "cursor-pointer"} ${active ? "surface-900 text-white mobile-brand-shadow font-bold" : "surface-100 text-500"}`} onClick={() => onChange((current) => ({...current, secondary: option.value}))}>{option.label}</button>;
                })}
            </div>
        </section>
    );
}

function TaskCard({task, loading, navigate, onToggleDone, readOnly, translate}) {
    return (
        <article className="w-full flex align-items-center gap-2 surface-card border-1 border-100 mobile-brand-radius p-2 mobile-brand-shadow">
            <button type="button" className={`w-2rem h-2rem flex align-items-center justify-content-center border-circle flex-shrink-0 cursor-pointer ${task.taskStatus === "done" ? "border-none bg-green-50 text-green-500" : "border-1 border-300 surface-card text-500"}`} disabled={readOnly || loading} aria-label={translate(task.taskStatus === "done" ? "mobile.tasks.restore" : "set-task-done")} onClick={() => onToggleDone(task)}><i className={loading ? "pi pi-spin pi-spinner text-xs" : task.taskStatus === "done" ? "pi pi-check text-xs" : "pi text-xs"}/></button>
            <button type="button" className="flex-1 min-w-0 text-right border-none surface-card p-1 cursor-pointer" onClick={() => navigate(`/mobile/tasks/${entityKey(task)}`)}>
                <strong className={`block text-xs text-900 mb-1 text-overflow-ellipsis overflow-hidden white-space-nowrap ${task.taskStatus === "done" ? "line-through text-400" : ""}`}>{task.title}</strong><RecurrenceBadge task={task}/>
                <small className="block text-500 text-overflow-ellipsis overflow-hidden white-space-nowrap">{richTextToPlain(task.description) || translate("mobile-shell.task.description-fallback")}</small>
                {task.projects?.[0] && <span className="flex align-items-center gap-1 mt-2 text-2xs text-500"><ProjectIndicator project={task.projects[0]} className="w-1rem h-1rem"/>{task.projects[0].title}</span>}
            </button>
            <span className="flex flex-column align-items-end gap-2 flex-shrink-0">
                <PriorityTag priority={task.mobilePriority || getPriority(task)} translate={translate}/>
                {task.dueDate && <TaskDateTime value={task.dueDate} className="text-3xs text-500"/>}
                <TaskFocusControls task={task} compact/>
            </span>
        </article>
    );
}

function MobileTaskDetail({detailId, tasks, refresh, navigate, translate, readOnly}) {
    const dispatch = useDispatch();
    const taskApi = useTask();
    const [task, setTask] = useState(() => tasks.find((item) => String(entityKey(item)) === String(detailId)));
    const [loading, setLoading] = useState(!task);
    const [mutationLoading, setMutationLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const existing = tasks.find((item) => String(entityKey(item)) === String(detailId));
        if (existing) {
            setTask(existing);
            setLoading(false);
            return;
        }
        let active = true;
        setLoading(true);
        taskApi.getTask(detailId).then((result) => {
            if (active) setTask(result);
        }).catch(setError).finally(() => active && setLoading(false));
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailId, tasks]);

    const toggleDone = async () => {
        if (!task || readOnly) return;
        const nextStatus = task.taskStatus === "done" ? "todo" : "done";
        setMutationLoading(true);
        setError(null);
        try {
            const updated = await taskApi.updateTask(entityKey(task), {
                taskStatus: nextStatus,
                doneAt: nextStatus === "done" ? Date.now() : null,
            });
            if (!updated) throw new Error("task-update-failed");
            dispatch(upsertWorkspaceTask(updated));
            setTask(updated);
            await refresh();
        } catch (requestError) {
            setError(requestError);
        } finally {
            setMutationLoading(false);
        }
    };

    if (loading) return <MobileLoading/>;
    if (!task) return <MobileError message={translate("mobile.tasks.detail-not-found")} retryLabel={translate("mobile.common.back")} onRetry={() => navigate("/mobile/tasks")}/>;

    return (
        <div className="flex flex-column gap-3">
            <MobilePageHeader title={task.title} description={translate("mobile.tasks.detail-description")} onBack={() => navigate(-1)}/>
            {error && <Message severity="error" className="text-xs" text={translate("mobile.common.mutation-error")}/>} 
            <section className="surface-900 text-white mobile-brand-radius p-4 mobile-brand-shadow">
                <div className="flex justify-content-between align-items-center gap-2 mb-3">
                    <PriorityTag priority={getPriority(task)} translate={translate}/>
                    <span className="text-xs text-300">{translate(task.taskStatus || "todo")}</span>
                </div>
                <RichTextView className="text-xs text-300 line-height-3 m-0" value={task.description} empty={<p className="text-xs text-300 line-height-3 m-0">{translate("mobile-shell.task.description-fallback")}</p>}/>
            </section>
            <section className="surface-card border-1 border-100 mobile-brand-radius p-3">
                <DetailRow label={translate("dueDate")} value={task.dueDate ? <TaskDateTime value={task.dueDate}/> : translate("mobile-shell.task.no-deadline")}/>
                <DetailRow label={translate("projects")} value={task.projects?.map((project) => project.title).join(", ") || translate("mobile-shell.task.no-project")}/>
                <DetailRow label={translate("assignee")} value={task.assignee?.member?.fullname || translate("not-selected")}/>
            </section>
            <TaskFocusControls task={task}/>
            {!readOnly && <Button type="button" className="w-full text-xs gap-2" icon={task.taskStatus === "done" ? "pi pi-undo" : "pi pi-check"} label={translate(task.taskStatus === "done" ? "mobile.tasks.restore" : "set-task-done")} loading={mutationLoading} onClick={toggleDone}/>} 
        </div>
    );
}

function DetailRow({label, value}) {
    return <div className="flex justify-content-between align-items-center gap-3 py-3 border-bottom-1 border-100"><span className="text-xs text-500">{label}</span><strong className="text-xs text-900 text-left">{value}</strong></div>;
}
